import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { DocumentRecord, DocumentCreate, DocumentUpdate } from '../../models/Document'

import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetDocumentsRequest extends BasePageRequest {
  searchTerm?: string
  contactId?: string
  createdByUserIds?: string
  documentTypeId?: string
  createdAfter?: string
  createdBefore?: string
  fileName?: string
  fileHash?: string
}

export interface GetAllDocumentsRequest extends BaseRequest {
  searchTerm?: string
  contactId?: string
  createdByUserIds?: string
  documentTypeId?: string
  createdAfter?: string
  createdBefore?: string
  fileName?: string
  fileHash?: string
}

export interface GetDocumentByIdRequest extends BaseRequest {
  documentId: string
}

export interface CreateDocumentRequest extends BaseRequest {
  payload: DocumentCreate
}

export interface UpdateDocumentRequest extends BaseRequest {
  documentId: string
  payload: DocumentUpdate
}

export interface DeleteDocumentRequest extends BaseRequest {
  documentId: string
}

export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Document'],
  endpoints: (builder) => ({
    getDocuments: builder.query<BasePageResponse<DocumentRecord[]>, GetDocumentsRequest>({
      query: (request) => {
        const { accountId, pageRequest, searchTerm, contactId, createdByUserIds, documentTypeId, createdAfter, createdBefore, fileName, fileHash } = request

        return {
          url: `/accounts/${accountId}/documents`,
          params: {
            page: pageRequest.page,
            size: pageRequest.size,
            ...(searchTerm ? { searchTerm } : {}),
            ...(contactId ? { contactId } : {}),
            ...(createdByUserIds ? { createdByUserIds } : {}),
            ...(documentTypeId ? { documentTypeId } : {}),
            ...(createdAfter ? { createdAfter } : {}),
            ...(createdBefore ? { createdBefore } : {}),
            ...(fileName ? { fileName } : {}),
            ...(fileHash ? { fileHash } : {}),
          },
        }
      },
      providesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    getAllDocuments: builder.query<BaseResponse<DocumentRecord[]>, GetAllDocumentsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, searchTerm, contactId, createdByUserIds, documentTypeId, createdAfter, createdBefore, fileName, fileHash } = request

        return await paginate<DocumentRecord>(
          {
            url: `/accounts/${accountId}/documents`,
            params: {
              ...(searchTerm ? { searchTerm } : {}),
              ...(contactId ? { contactId } : {}),
              ...(createdByUserIds ? { createdByUserIds } : {}),
              ...(documentTypeId ? { documentTypeId } : {}),
              ...(createdAfter ? { createdAfter } : {}),
              ...(createdBefore ? { createdBefore } : {}),
              ...(fileName ? { fileName } : {}),
              ...(fileHash ? { fileHash } : {}),
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    getDocumentById: builder.query<DocumentRecord, GetDocumentByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/documents/${request.documentId}`,
      transformResponse: (response: BaseResponse<DocumentRecord>) => {
        return response.data
      },
      providesTags: (_result, _error, request) => [{ type: 'Document', id: request.documentId }],
    }),
    createDocument: builder.mutation<DocumentRecord, CreateDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentRecord>) => {
        return response.data
      },
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    updateDocument: builder.mutation<DocumentRecord, UpdateDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentRecord>) => {
        return response.data
      },
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    deleteDocument: builder.mutation<void, DeleteDocumentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/documents/${request.documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetDocumentsQuery,
  useLazyGetDocumentsQuery,
  useLazyGetAllDocumentsQuery,
  useGetAllDocumentsQuery,
  useGetDocumentByIdQuery,
  useLazyGetDocumentByIdQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
} = documentsApi
